import React from 'react';
import { IconDevicePhoneOutline, IconPlusOutline } from '@saucelabs/ds-icons';
import { Box, Col, Row, Flex } from '@saucelabs/sl-component-grid';
import { Button } from '@saucelabs/ds-button';
import { LabelPill } from '@saucelabs/sl-component-label';
import { IconBulletChecked } from '@saucelabs/sl-component-icons';
import { AnimatePresence, motion } from 'framer-motion';

import {
  PLAN_INTERVAL,
  PRODUCT_MOBILE_QUALITY,
  TRANSITION,
  FADE,
  INTERVAL_KEY,
} from '../../../constants';
import { PlanConfiguration } from '../../../records/PlanConfiguration';
import { Product } from '../Product';
import { SectionTitle } from '../../Layout/SectionTitle';
import { FeatureList } from '../../Plans/FeatureList';
import { PriceColumn } from '../PriceColumn';
import { getIntervalUnit } from '../../../utils';

interface MobileQualityTileProps {
  onMobileQualityToggle: void
  hasMobileQualityInCart: boolean
  hasMobileQualitySubscription: boolean
  mobileQualityConfig: PlanConfiguration
  mobileQualityPlans: Record<string, Plan>
  showComparisonMobileQuality: void
  updateProductConfig: void
  price: number
}
const MobileQualityTile: React.FC<MobileQualityTileProps> = ({
  onMobileQualityToggle,
  hasMobileQualityInCart,
  hasMobileQualitySubscription,
  mobileQualityConfig,
  mobileQualityPlans,
  showComparisonMobileQuality,
  updateProductConfig,
  price,
}) => (
  <Box.Section
    className="text-high-emphasis"
    data-qa={`product-section-${PRODUCT_MOBILE_QUALITY}`}
  >
    <Row>
      <Col xs>
        <Flex
          alignItems="center"
          justifyContent="space-between"
        >
          <SectionTitle>
            <IconDevicePhoneOutline className="h-3 fill-current text-gray-500" />
            Mobile Beta Testing - TestFairy
            <LabelPill variant="light-gray">New</LabelPill>
          </SectionTitle>

          <AnimatePresence
            exitBeforeEnter
            initial={false}
          >
            {(hasMobileQualityInCart || hasMobileQualitySubscription) && (
              <motion.div
                key="plans"
                animate="animate"
                exit="exit"
                initial="initial"
                transition={TRANSITION}
                variants={FADE}
                y={7}
              >
                <Product.IntervalChoice
                  id={PRODUCT_MOBILE_QUALITY}
                  onChange={(isMonthly) => {
                    updateProductConfig({
                      [INTERVAL_KEY]: getIntervalUnit(isMonthly),
                      product: PRODUCT_MOBILE_QUALITY,
                    });
                  }}
                  saving="20%"
                  selectedInterval={mobileQualityConfig.interval}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </Flex>
      </Col>
    </Row>
    <Row className="mt-1">
      <Col xs={9}>
        <p className="mb-0 text-base text-gray-600">
          Automate your beta-testing app distribution,
          streamline your development process, manage feedback cycles,
          and deliver faster &amp; better to market.
        </p>
      </Col>

    </Row>
    <Row between="xs">
      <Col xs>
        <div className="mt-2 mb-1">
          <strong>Startup plan includes:</strong>
        </div>
        <FeatureList>
          <li>
            <IconBulletChecked />
            50 Apps
          </li>
          <li>
            <IconBulletChecked />
            Session Video Recording
          </li>
          <li>
            <IconBulletChecked />
            500 Beta Testers
          </li>
          <li>
            <IconBulletChecked />
            In-App Feedback
          </li>
        </FeatureList>
        <a
          className="inline-block mt-2 cursor-pointer text-blue-700"
          data-qa="mobile-quality-modal-button"
          onClick={showComparisonMobileQuality}
          role="button"
          tabIndex={0}
        >
          Compare Plans
        </a>
      </Col>
      <PriceColumn
        hasStartingPrice={false}
        price={mobileQualityConfig.interval === PLAN_INTERVAL.YEAR ?
          price : mobileQualityPlans[PLAN_INTERVAL.MONTH]?.price}
      >
        {!hasMobileQualitySubscription && hasMobileQualityInCart && (
          <Button
            color="secondary"
            elementType="button"
            onClick={onMobileQualityToggle}
            size="md"
            type="button"
            variant="outline"
          >
            <strong>Remove Plan</strong>
          </Button>
        )}
        {!hasMobileQualitySubscription && !hasMobileQualityInCart && (
          <Button
            color="primary"
            elementType="button"
            onClick={onMobileQualityToggle}
            prefixIcon={<IconPlusOutline />}
            size="md"
            type="button"
            variant="outline"
          >
            Add Plan
          </Button>
        )}
      </PriceColumn>
    </Row>
  </Box.Section>
);

export { MobileQualityTile };
